"use client";
/*
 * RepairGuideSection.tsx — 修理手順ステップUIコンポーネント
 * Design: テクニカル・マニュアル × ダーク精密系
 * - タブで修理種別（画面・バッテリー・充電ポート・カメラ）を切り替え
 * - 縦ステッパー形式で手順を表示
 * - ステップをクリックで展開/折りたたみ
 */

import { useState } from "react";
import Link from "next/link";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Monitor, Battery, Plug, Camera, Wrench, ExternalLink } from "lucide-react";
import type { RepairGuide } from "@/hooks/useRepairSteps";

const GUIDE_ICONS: Record<string, React.ReactNode> = {
  screen: <Monitor className="w-4 h-4" />,
  battery: <Battery className="w-4 h-4" />,
  charging: <Plug className="w-4 h-4" />,
  camera: <Camera className="w-4 h-4" />,
};

const STEP_COLORS = [
  "bg-blue-500",
  "bg-indigo-500",
  "bg-violet-500",
  "bg-purple-500",
  "bg-fuchsia-500",
  "bg-pink-500",
  "bg-rose-500",
  "bg-orange-500",
  "bg-amber-500",
];

interface Props {
  guides: RepairGuide[];
  modelId?: number;
}

export function RepairGuideSection({ guides, modelId }: Props) {
  const [activeTab, setActiveTab] = useState(guides[0]?.id ?? "");
  const [openStep, setOpenStep] = useState<number | null>(null);

  const activeGuide = guides.find(g => g.id === activeTab) ?? guides[0];

  return (
    <section className="mt-6 rounded-lg border border-border/60 overflow-hidden"
      style={{ background: "oklch(0.14 0.015 260)" }}>
      {/* ── セクションヘッダー ── */}
      <div className="flex items-center gap-2.5 px-5 py-4 border-b border-border/50">
        <Wrench className="w-4 h-4 text-primary" />
        <h2 className="font-semibold text-sm tracking-wide"
          style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          修理手順ガイド
        </h2>
        <span className="ml-auto text-xs font-mono text-muted-foreground">
          {activeGuide?.steps.length ?? 0} STEPS
        </span>
      </div>

      {/* ── タブ ── */}
      <div className="border-b border-border/50 overflow-x-auto" style={{ WebkitOverflowScrolling: "touch" }}>
        <div className="flex min-w-max">
          {guides.map(guide => (
            <div key={guide.id} className="flex items-stretch">
              <button
                onClick={() => { setActiveTab(guide.id); setOpenStep(null); }}
                className={`flex items-center gap-2 px-4 py-3 text-xs font-mono whitespace-nowrap transition-colors border-b-2 ${
                  activeTab === guide.id
                    ? "border-primary text-primary bg-primary/5"
                    : "border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/20"
                }`}
              >
                {GUIDE_ICONS[guide.id] ?? <Wrench className="w-4 h-4" />}
                <span>{guide.title}</span>
              </button>
              {modelId && (
                <Link
                  href={`/models/${modelId}/${guide.id}`}
                  className="flex items-center px-2 text-muted-foreground/50 hover:text-primary transition-colors border-b-2 border-transparent"
                  title={`${guide.title}の専用ページ`}
                >
                  <ExternalLink className="w-3 h-3" />
                </Link>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* ── ステップリスト ── */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.2 }}
          className="p-5"
        >
          {/* 完了進捗バー */}
          <div className="mb-5 flex items-center gap-3">
            <div className="flex-1 h-1.5 rounded-full bg-muted/40 overflow-hidden">
              <motion.div
                className="h-full rounded-full bg-primary"
                initial={{ width: 0 }}
                animate={{ width: openStep !== null ? `${((openStep + 1) / (activeGuide?.steps.length ?? 1)) * 100}%` : "0%" }}
                transition={{ duration: 0.4 }}
              />
            </div>
            <span className="text-xs font-mono text-muted-foreground shrink-0">
              {openStep !== null ? `${openStep + 1} / ${activeGuide?.steps.length}` : `全 ${activeGuide?.steps.length} ステップ`}
            </span>
          </div>

          {/* ステップ一覧 */}
          <ol className="relative flex flex-col gap-0">
            {activeGuide?.steps.map((step, i) => {
              const isOpen = openStep === i;
              const isDone = openStep !== null && i < openStep;
              const dotColor = STEP_COLORS[i % STEP_COLORS.length];

              return (
                <li key={step.step} className="relative pl-10">
                  {/* 縦ライン */}
                  {i < (activeGuide.steps.length - 1) && (
                    <div className={`absolute left-[14px] top-7 w-0.5 h-full -translate-x-1/2 transition-colors duration-300 ${
                      isDone ? "bg-primary/60" : "bg-border/40"
                    }`} />
                  )}

                  {/* ステップ番号ドット */}
                  <div className={`absolute left-0 top-3 w-7 h-7 rounded-full flex items-center justify-center text-xs font-mono font-bold transition-all duration-300 ${
                    isDone
                      ? "bg-primary/30 text-primary border border-primary/50"
                      : isOpen
                      ? `${dotColor} text-white shadow-lg`
                      : "bg-muted/30 text-muted-foreground border border-border/50"
                  }`}>
                    {isDone ? "✓" : step.step}
                  </div>

                  {/* ステップカード */}
                  <div className={`mb-2 rounded-lg border transition-all duration-200 overflow-hidden ${
                    isOpen
                      ? "border-primary/40 bg-primary/5"
                      : isDone
                      ? "border-primary/20 bg-primary/3"
                      : "border-border/40 bg-muted/10 hover:border-border/70"
                  }`}>
                    <button
                      onClick={() => setOpenStep(isOpen ? null : i)}
                      className="w-full flex items-center justify-between px-4 py-3 text-left"
                    >
                      <span className={`text-sm font-medium transition-colors ${
                        isOpen ? "text-foreground" : isDone ? "text-foreground/60 line-through" : "text-foreground/80"
                      }`}>
                        {step.title}
                      </span>
                      <ChevronDown className={`w-4 h-4 flex-shrink-0 ml-2 transition-transform duration-200 text-muted-foreground ${
                        isOpen ? "rotate-180" : ""
                      }`} />
                    </button>

                    <AnimatePresence>
                      {isOpen && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="overflow-hidden"
                        >
                          <div className="px-4 pb-4 pt-0">
                            <div className="border-t border-border/30 pt-3">
                              <p className="text-sm text-foreground/75 leading-relaxed">
                                {step.desc}
                              </p>
                              {/* 次のステップへボタン */}
                              {i < (activeGuide.steps.length - 1) && (
                                <button
                                  onClick={() => setOpenStep(i + 1)}
                                  className="mt-3 inline-flex items-center gap-1.5 text-xs font-mono text-primary hover:text-primary/80 transition-colors"
                                >
                                  次のステップへ →
                                </button>
                              )}
                              {i === activeGuide.steps.length - 1 && (
                                <div className="mt-3 inline-flex items-center gap-1.5 text-xs font-mono text-emerald-400">
                                  ✓ 修理完了
                                </div>
                              )}
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </li>
              );
            })}
          </ol>
        </motion.div>
      </AnimatePresence>
    </section>
  );
}
